import {
  applyImagePositionToElement,
  normalizeOptionalImagePosition
} from '../Utils/imagePosition'
import { normalizeMedia } from '../Utils/mediaValue'

/**
 * Display web component for crop-mode="position" photos.
 *
 * AppM styles the host (size, radius, border, shadow). ABR owns inner framing.
 *
 * Preferred:
 *   <anear-framed-image media='{"url":"...","filename":null,"position":{...}}' alt="..."></anear-framed-image>
 *
 * Legacy fallback:
 *   <anear-framed-image src="..." position='{"x":40,"y":82,"zoom":1.6}' alt="..."></anear-framed-image>
 */
class AnearFramedImage extends HTMLElement {
  static get observedAttributes() {
    return ['media', 'src', 'position', 'alt']
  }

  connectedCallback() {
    this.#ensureHostStyles()
    this.render()
  }

  attributeChangedCallback() {
    if (!this.isConnected) return
    this.render()
  }

  #ensureHostStyles() {
    this.style.display = this.style.display || 'block'
    this.style.overflow = 'hidden'
  }

  get media() {
    return this.getAttribute('media') || ''
  }

  get src() {
    return this.getAttribute('src') || ''
  }

  get position() {
    return this.getAttribute('position') || ''
  }

  get alt() {
    return this.getAttribute('alt') || ''
  }

  /** Prefer opaque media; fall back to src + position. */
  resolveMedia() {
    const fromMedia = normalizeMedia(this.media)
    if (fromMedia) return fromMedia

    const src = this.src.trim()
    if (!src) return null

    return {
      url: src,
      filename: null,
      position: normalizeOptionalImagePosition(this.position)
    }
  }

  render() {
    this.#ensureHostStyles()

    const media = this.resolveMedia()
    const src = media?.url?.trim() || ''
    if (!src) {
      this.replaceChildren()
      return
    }

    let img = this.querySelector('img')
    if (!img) {
      img = document.createElement('img')
      img.style.width = '100%'
      img.style.height = '100%'
      img.style.display = 'block'
      this.replaceChildren(img)
    }

    if (img.getAttribute('src') !== src) {
      img.src = src
    }
    img.alt = this.alt

    const framing = media?.position || null
    if (framing) {
      applyImagePositionToElement(img, framing)
    } else {
      img.style.objectFit = 'cover'
      img.style.objectPosition = ''
      img.style.transform = ''
      img.style.transformOrigin = ''
    }
  }
}

if (!customElements.get('anear-framed-image')) {
  customElements.define('anear-framed-image', AnearFramedImage)
}

export default AnearFramedImage
